import { motion } from 'framer-motion';
import {
  ArrowRight,
  Heart,
  Package,
  Search,
  ShoppingCart,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HomeBannerSlider from '../../components/website/HomeBannerSlider';
import HomeCategoryNav from '../../components/website/HomeCategoryNav';
import MicronLogo from '../../components/website/MicronLogo';
import { useHomeCategories } from '../../hooks/useHomeCategories';
import {
  categorySections,
  dealProducts,
  navLinks,
} from './homeData';

function useCountdown(target) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };

  const [time, setTime] = useState(calc);

  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);

  return time;
}

function ProductCard({ name, price, image, large = false }) {
  return (
    <motion.article
      whileHover={{ y: -6 }}
      className="group overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-neutral-200/80 transition-shadow hover:shadow-lg hover:ring-red-100"
    >
      <div className={`relative overflow-hidden bg-neutral-50 ${large ? 'aspect-square' : 'aspect-[4/3]'}`}>
        <img
          src={image}
          alt={name}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <button
          type="button"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-neutral-400 shadow transition hover:text-micron-red"
          aria-label="Save item"
        >
          <Heart className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4">
        <h3 className="truncate font-semibold text-micron-black">{name}</h3>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-lg font-bold text-micron-red">USD {price}</span>
          <button
            type="button"
            className="rounded-lg bg-micron-black px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-micron-red"
          >
            Add
          </button>
        </div>
      </div>
    </motion.article>
  );
}

function DealCard({ name, discount, image }) {
  return (
    <motion.article
      whileHover={{ scale: 1.02 }}
      className="relative min-w-[160px] shrink-0 overflow-hidden rounded-xl bg-white p-4 shadow-md sm:min-w-0"
    >
      <span className="absolute left-3 top-3 rounded-full bg-micron-red px-2 py-0.5 text-[10px] font-bold text-white">
        -{discount}%
      </span>
      <img src={image} alt={name} className="mx-auto h-28 object-contain" />
      <p className="mt-3 truncate text-center text-sm font-medium text-micron-black">{name}</p>
    </motion.article>
  );
}

export default function Home2() {
  const { categories, loading: categoriesLoading, activeId, setActiveId } = useHomeCategories();
  const countdown = useCountdown(
    new Date(Date.now() + 4 * 86400000 + 13 * 3600000 + 34 * 60000 + 56 * 1000),
  );

  return (
    <div className="min-h-screen bg-white font-['Plus_Jakarta_Sans',sans-serif] text-micron-black">
      {/* Compact header */}
      <header className="sticky top-0 z-50 border-b border-neutral-100 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
          <MicronLogo className="h-9 w-auto" />

          <div className="relative mx-auto hidden max-w-md flex-1 md:block">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="search"
              placeholder="Search electronics..."
              className="w-full rounded-full border border-neutral-200 bg-neutral-50 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-micron-red focus:ring-2 focus:ring-red-100"
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <button type="button" className="hidden rounded-full p-2 text-neutral-600 hover:bg-neutral-100 sm:block" aria-label="Orders">
              <Package className="h-5 w-5" />
            </button>
            <button type="button" className="relative rounded-full p-2 text-neutral-600 hover:bg-neutral-100" aria-label="Cart">
              <ShoppingCart className="h-5 w-5" />
              <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-micron-red text-[9px] font-bold text-white">
                0
              </span>
            </button>
            <Link
              to="/signin"
              className="hidden rounded-full bg-micron-red px-4 py-2 text-sm font-semibold text-white transition hover:bg-micron-red-dark sm:inline-block"
            >
              Sign in
            </Link>
            <Link to="/signin" className="rounded-full p-2 text-neutral-600 hover:bg-neutral-100 sm:hidden" aria-label="Sign in">
              <User className="h-5 w-5" />
            </Link>
          </div>
        </div>

        <nav className="overflow-x-auto border-t border-neutral-50">
          <div className="mx-auto flex max-w-7xl gap-1 px-4 py-2 sm:px-6">
            {navLinks.map((link) => (
              <a
                key={link}
                href="#"
                className="shrink-0 rounded-full px-4 py-1.5 text-sm text-neutral-600 transition hover:bg-red-50 hover:text-micron-red"
              >
                {link}
              </a>
            ))}
            <Link
              to="/"
              className="ml-auto shrink-0 rounded-full bg-neutral-100 px-4 py-1.5 text-sm font-medium text-neutral-600 hover:bg-neutral-200"
            >
              Home v1
            </Link>
          </div>
        </nav>
      </header>

      {/* Full-width hero */}
      <section className="mx-auto max-w-7xl px-4 pt-4 sm:px-6">
        <HomeBannerSlider autoPlayMs={5500} minHeight="min-h-[320px] sm:min-h-[400px]" />
      </section>

      {/* Category pills */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <h2 className="mb-4 text-lg font-bold text-micron-black">Shop by category</h2>
        <HomeCategoryNav
          variant="pills"
          categories={categories}
          loading={categoriesLoading}
          activeId={activeId}
          onSelect={setActiveId}
        />
      </section>

      {/* Flash deals — full-width red band */}
      <section id="deals" className="bg-micron-red py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-red-200">Limited time</p>
              <h2 className="text-2xl font-extrabold text-white sm:text-3xl">Flash Deals</h2>
            </div>
            <div className="flex gap-3">
              {[
                { v: countdown.days, l: 'D' },
                { v: countdown.hours, l: 'H' },
                { v: countdown.minutes, l: 'M' },
                { v: countdown.seconds, l: 'S' },
              ].map(({ v, l }) => (
                <div key={l} className="flex flex-col items-center">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 text-lg font-bold text-white backdrop-blur-sm">
                    {String(v).padStart(2, '0')}
                  </span>
                  <span className="mt-1 text-[10px] text-red-200">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-1 sm:grid sm:grid-cols-5 sm:overflow-visible">
            {dealProducts.map((p) => (
              <DealCard key={p.name} {...p} />
            ))}
          </div>
        </div>
      </section>

      {/* Product sections — card grid style */}
      {categorySections.map((section, idx) => (
        <section key={section.title} className={`py-14 ${idx % 2 === 0 ? 'bg-neutral-50' : 'bg-white'}`}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div className="flex items-center gap-5">
                <img
                  src={section.featureImage}
                  alt=""
                  className="hidden h-20 w-20 rounded-2xl object-cover shadow-md sm:block"
                />
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-micron-red">Collection</p>
                  <h2 className="text-2xl font-extrabold text-micron-black sm:text-3xl">{section.title}</h2>
                </div>
              </div>
              <motion.button
                whileHover={{ x: 4 }}
                type="button"
                className="inline-flex items-center gap-1 text-sm font-semibold text-micron-red"
              >
                View all
                <ArrowRight className="h-4 w-4" />
              </motion.button>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {section.products.map((p) => (
                <ProductCard key={p.name} {...p} large={idx === 1} />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA banner */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="overflow-hidden rounded-3xl bg-micron-black px-8 py-12 text-center sm:px-16">
          <h2 className="text-2xl font-extrabold text-white sm:text-4xl">Upgrade your tech today</h2>
          <p className="mx-auto mt-3 max-w-md text-neutral-400">
            Free shipping on orders over USD 75. brixECOM — trusted electronics since day one.
          </p>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            className="mt-6 rounded-full bg-micron-red px-8 py-3 text-sm font-bold text-white hover:bg-micron-red-dark"
          >
            Start shopping
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-200 bg-neutral-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <MicronLogo className="h-10 w-auto" />
            <div className="flex flex-wrap gap-6 text-sm text-neutral-500">
              <a href="#" className="hover:text-micron-red">Help</a>
              <a href="#" className="hover:text-micron-red">About</a>
              <Link to="/signin" className="hover:text-micron-red">Seller login</Link>
              <Link to="/" className="hover:text-micron-red">Home v1</Link>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-neutral-400">
            © {new Date().getFullYear()} brixECOM. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
